import React, { useState } from "react";
import {
  Box,
  HStack,
  Menu,
  Button,
  MenuButton,
  MenuItem,
  MenuList,
  Avatar,
  Text,
  useBreakpointValue,
  Flex,
  Spinner,
} from "@chakra-ui/react";
import {
  AccountOutline,
  FormSelect,
  Logout,
  ChevronDown,
  ChevronUp,
} from "../../../assets/icons/components";
import { Link, useLocation } from "react-router-dom";
import { userSignout } from "../../../api/utils/axios";
import ModalTemplate from "../general/ModalTemplate";
import useCleverTap, {
  CleverTapEvents,
  ScreenNames,
  ButtonTypes,
  ModalNames,
} from "../../../clevertap/useCleverTap";
import { sendTrackingEvent } from "../utils/gtm";

interface Props {
  username?: string;
  children?: string[] | null;
  imgSrc?: string;
}

const iconProps = {
  boxSize: "24px",
  color: "ms-grey.700",
};

export const HeaderRightSection = ({ username, imgSrc }: Props) => {
  const desktopScreens = useBreakpointValue({ base: "none", md: "block" });
  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const clevertap = useCleverTap()[0];
  const location = useLocation();
  const profileLinks = [
    {
      id: "rightHeaderMenu__myProfile",
      title: "My Profile",
      link: "/profile",
      icon: <AccountOutline {...iconProps} />,
      onClick: undefined,
      gtmId: "myProfile",
    },
    {
      id: "rightHeaderMenu__myApplications",
      title: "My Application",
      link: "/profile/applications",
      icon: <FormSelect {...iconProps} />,
      onClick: undefined,
      gtmId: "myApplications",
    },

    {
      id: "rightHeaderMenu__signOut",
      title: "Sign out",
      link: null,
      icon: <Logout {...iconProps} />,
      gtmId: "signOut",
      onClick: () => {
        setOpen(true);
        // clever tap event
        clevertap.fireEvent(CleverTapEvents.click_profile_navigation, {
          screen_name: ScreenNames.dashboardCourses,
          button_text: "sign out",
          button_type: ButtonTypes.link,
          button_sequence: 4,
        });
        clevertap.fireEvent(CleverTapEvents.viewed_modal, {
          modal_name: ModalNames.signOut,
        });
      },
    },
  ];

  const handleSignout = async () => {
    setLoading(true);
    await userSignout({ redirectTrue: false });
    setLoading(false);
    sendTrackingEvent({ event: "dash-sign_out-yes", eventLabel: location.pathname });
    clevertap.fireEvent(CleverTapEvents.click_sign_out, {
      modal_name: ModalNames.signOut,
      button_text: "sign_out",
      button_type: ButtonTypes.primary,
    });
  };

  const onClickProfileIconTrack = () => {
    // clever tap event
    clevertap.fireEvent(CleverTapEvents.click_profile_icon, {
      screen_name: ScreenNames.dashboardCourses,
      button_type: "dropdown",
    });
  };

  const onClickProfileMenuTrack = (text: string) => {
    const btnText = text.toLowerCase();
  };

  const handleSendEvent = (gtmId: string) => {
    switch (gtmId) {
      case "myProfile":
        sendTrackingEvent({ event: "dash-my_profile", eventLabel: location.pathname });
        break;
      case "myApplications":
        sendTrackingEvent({ event: "dash-my_application", eventLabel: location.pathname });
        break;
      case "signOut":
        sendTrackingEvent({ event: "dash-sign_out", eventLabel: location.pathname });
        break;
      default:
        return;
    }
  };

  return (
    <>
      <ModalTemplate
        title="Are you sure you want to sign out?"
        isOpen={open}
        onClose={() => {
          sendTrackingEvent({ event: "dash-sign_out-X", eventLabel: location.pathname });
          setOpen(false);
          clevertap.fireEvent(CleverTapEvents.click_close_modal, {
            modal_name: ModalNames.signOut,
          });
        }}
        showCloseButtonIcon={true}
        showFooterContent={true}
        footerContent={
          <Flex mt="2rem" gap="1rem">
            <Button
              variant="secondary"
              onClick={() => {
                setOpen(false);
                sendTrackingEvent({ event: "dash-sign_out-cancel", eventLabel: location.pathname });
                clevertap.fireEvent(CleverTapEvents.click_cancel, {
                  modal_name: ModalNames.signOut,
                  button_text: "CANCEL",
                  button_type: ButtonTypes.secondary,
                });
              }}
            >
              Cancel
            </Button>
            <Button
              id="modalSignOutButton"
              disabled={loading}
              onClick={handleSignout}
              variant="primary"
              position={loading ? "relative" : "static"}
            >
              SIGN OUT
              {loading && (
                <Spinner
                  speed={"0.5s"}
                  thickness={"2px"}
                  size={"md"}
                  position={"absolute"}
                  color={"ms-blue.500"}
                />
              )}
            </Button>
          </Flex>
        }
      >
        <></>
      </ModalTemplate>
      <Menu autoSelect={false} placement="bottom-start" offset={[8, 4]}>
        {({ isOpen }) => (
          <>
            <Box
              onClick={() => {
                sendTrackingEvent({
                  event: "dash-candidate_profile",
                  eventLabel: location.pathname,
                });
              }}
              id="user-profile"
            >
              <MenuButton
                onClick={onClickProfileIconTrack}
                p="0"
                textTransform="initial"
                size="body1-md"
                as={Button}
                isActive={isOpen}
                border="none"
                _hover={{
                  bg: "none",
                }}
                _focus={{ border: "none", bg: "none" }}
                _active={{ bg: "none" }}
                rightIcon={
                  isOpen ? (
                    <Box display={desktopScreens}>
                      <ChevronUp color="ms-blue.500" boxSize="4" />
                    </Box>
                  ) : (
                    <Box display={desktopScreens}>
                      <ChevronDown color="ms-blue.500" boxSize="4" />
                    </Box>
                  )
                }
              >
                <Box>
                  <HStack id="rightHeader__menuButton">
                    <Avatar
                      size="xs"
                      bg="ms-purple.500"
                      color="white"
                      name={username}
                      fontWeight="bold"
                      src={imgSrc}
                      icon={<AccountOutline boxSize="6" />}
                    />
                    <Text display={desktopScreens} variant="body2-md" pt="ms-2">
                      {username}
                    </Text>
                  </HStack>
                </Box>
              </MenuButton>
            </Box>
            <MenuList borderRadius="ms-16">
              {profileLinks.map((item, idx) => {
                return item.link ? (
                  <Link
                    onClick={() => {
                      handleSendEvent(item.gtmId);
                      onClickProfileMenuTrack(item.title);
                    }}
                    to={item.link}
                    key={item.link}
                    id={item.id}
                  >
                    <MenuItem
                      boxShadow={profileLinks.length - 1 !== idx ? "0px 1px 0px #D9D9D9" : "none"}
                      _hover={{ bgColor: "ms-blue.50", boxShadow: "none", color: "ms-blue.500" }}
                      h="48px"
                      icon={item.icon}
                      iconSpacing="ms-8"
                      textStyle="body1"
                      color="ms-red.900"
                    >
                      {item.title}
                    </MenuItem>
                  </Link>
                ) : (
                  <MenuItem
                    key={idx}
                    onClick={() => {
                      handleSendEvent(item.gtmId);
                      item && item.onClick && item.onClick();
                    }}
                    boxShadow={profileLinks.length - 1 !== idx ? "0px 1px 0px #D9D9D9" : "none"}
                    _hover={{ bgColor: "ms-blue.50", boxShadow: "none", color: "ms-blue.500" }}
                    h="48px"
                    icon={item.icon}
                    iconSpacing="ms-8"
                    textStyle="body1"
                    color="ms-red.900"
                    className={item.id}
                  >
                    {item.title}
                  </MenuItem>
                );
              })}
            </MenuList>
          </>
        )}
      </Menu>
    </>
  );
};
